import { useId, useState } from "react";

import { AlertDialog, Button, Panel, Skeleton, Switch } from "@go-novus/ui";

import * as m from "#/paraglide/messages";

export type StorefrontBarProps =
  | {
      /** 配置还没回来：标题是静态文案摆真的，开关和入口的位置摆等高占位块。 */
      loading: true;
    }
  | {
      loading?: false;
      /** 草稿里的开关值。 */
      enabled: boolean;
      onChange: (next: boolean) => void;
      /** 草稿里的开关和已保存的不一样：旁边给一句「保存后生效」。 */
      unsaved: boolean;
      /** 主题编辑器入口，服务端拼好随配置下发；接口没给就不显示。 */
      themeEditorUrl: string | undefined;
    };

const BAR_PADDING = "12px 18px";

/**
 * 标签页上方常驻的店面行：开关决定挂件是否在店面出现，和外观设置不是一类东西。
 * 不展示上线状态——后台拿不到主题里 App Embed 是否启用，只给主题编辑器入口。
 */
export function StorefrontBar(props: StorefrontBarProps) {
  const [confirmingDisable, setConfirmingDisable] = useState(false);
  const hintId = useId();

  if (props.loading) {
    // 加载中的占位不带 group 角色：组里还没有真控件，标了名字的空组只是噪音。
    return (
      <div>
        {/* 内边距走 Panel 的 padding 属性：它以行内样式写出，class 里的 px/py 不生效。 */}
        <Panel
          className="flex flex-wrap items-center gap-x-4.5 gap-y-2"
          padding={BAR_PADDING}
        >
          <div className="flex min-w-0 grow basis-60 flex-wrap items-baseline gap-x-2.5">
            <span className="text-title [font-weight:var(--font-weight-semibold)]">
              {m.settings_visibility_title()}
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-2.5">
            {/* 占位块照抄真实控件（开关 38×22、按钮高 32），加载前后这条不换版式。 */}
            <Skeleton height={22} width={38} />
            <Skeleton height={32} width={140} />
          </div>
        </Panel>
      </div>
    );
  }

  const { enabled, onChange, unsaved, themeEditorUrl } = props;

  return (
    <div aria-label={m.settings_visibility_title()} role="group">
      {/* 内边距走 Panel 的 padding 属性：它以行内样式写出，class 里的 px/py 不生效。 */}
      <Panel
        className="flex flex-wrap items-center gap-x-4.5 gap-y-2"
        padding={BAR_PADDING}
      >
        <div className="flex min-w-0 grow basis-60 flex-wrap items-baseline gap-x-2.5">
          <span className="text-title [font-weight:var(--font-weight-semibold)]">
            {m.settings_visibility_title()}
          </span>
          {unsaved && (
            <span
              className="text-micro text-[var(--content-fg-subtle)]"
              id={hintId}
            >
              {m.settings_visibility_pending()}
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2.5">
          <Switch
            aria-describedby={unsaved ? hintId : undefined}
            checked={enabled}
            label={m.settings_visibility_title()}
            onChange={(next) => {
              // 关掉等于让店面不再展示挂件，先确认一次。
              if (next) {
                onChange(true);
              } else {
                setConfirmingDisable(true);
              }
            }}
          />
          {themeEditorUrl && (
            <Button
              as="a"
              href={themeEditorUrl}
              rel="noopener noreferrer"
              size="sm"
              target="_blank"
            >
              {m.settings_theme_editor()}
            </Button>
          )}
        </div>
      </Panel>
      <AlertDialog
        cancelLabel={m.settings_disable_cancel()}
        confirmLabel={m.settings_disable_confirm()}
        description={m.settings_disable_description()}
        onCancel={() => {
          setConfirmingDisable(false);
        }}
        onConfirm={() => {
          onChange(false);
          setConfirmingDisable(false);
        }}
        open={confirmingDisable}
        title={m.settings_disable_title()}
        tone="danger"
      />
    </div>
  );
}
